import {
  AlertTriangle,
  BarChart3,
  BookOpen,
  Box,
  Calendar,
  Check,
  CheckCircle2,
  Clock,
  Copy,
  FileText,
  Folder,
  Heart,
  Home,
  Info,
  MapPin,
  MessageSquare,
  ShoppingBag,
  SlidersHorizontal,
  Target,
  Ticket,
  Truck,
  User,
  Users,
  Wallet,
  type LucideIcon,
  type LucideProps,
} from 'lucide-react';
import { createElement } from 'react';
import ptBR from './pt-BR.json';

/**
 * Os 6 exemplos de negócio da bancada do estúdio (ADR 0133, parte 3), tirados do protótipo: o
 * menu, os números, a lista, o registro, o quadro, a agenda, a galeria, o cadastro em etapas, as
 * conversas e o relatório de cada um. São dados de exemplo, num arquivo por idioma; sem o
 * arquivo do idioma, vale o pt-BR (os nomes e valores são de empresas brasileiras).
 */

/** Situação: aprovado, atenção, informação, problema, neutra. */
export type Tone = 'ok' | 'warn' | 'info' | 'bad' | 'neu';
type Status = [string, Tone];

export interface SampleArea {
  id: string;
  icon: string;
  label: string;
  go?: string;
  children?: string[];
  count?: number;
  cap?: string;
}

export interface SampleRow {
  id: string;
  title: string;
  c3: string;
  who: string;
  d1: string;
  d2: string;
  st: Status;
  val: string;
}

export interface SampleDomain {
  id: string;
  name: string;
  desc: string;
  icon: string;
  /** Nome da empresa, a linha de baixo e as iniciais. */
  brand: [string, string, string];
  /** O logo do sistema, quando a prévia mostra a marca de quem usa o estúdio. */
  logo?: string;
  /** Quem está usando: nome, cargo e iniciais. */
  user: [string, string, string];
  tagline: string;
  areas: SampleArea[];
  mainArea: string;
  mainChild: string;
  list: {
    title: string;
    sub: string;
    total: number;
    newL: string;
    /** Cabeçalho, campo da linha e jeito de mostrar (strong, mut, dmut, badge, num, id). */
    cols: [string, keyof SampleRow, string][];
    slim: (keyof SampleRow)[];
  };
  rows: SampleRow[];
  mainIdx: number;
  painelSub: string;
  /** Rótulo, valor, para cima ou para baixo, variação, nota e ícone. */
  kpis: [string, string, 'up' | 'dn', string, string, string][];
  chart: { title: string; unit: string; values: number[] };
  hbar: { title: string; rows: [string, number, Tone][] };
  top: { title: string; cols: string[]; rows: string[][] };
  todo: [Tone, string, string][];
  board: Status[];
  rec: {
    tabs: [string, number?][];
    alert: [Tone, string, string, string];
    fields: [string, string, string?][];
    sub: { title: string; cols: [string, string][]; rows: string[][] };
    summary: [string, string][];
    big: [string, string | null, string];
    tl: [string, string, Tone][];
    primary: [string, string];
    secondary: [string, string];
    danger?: [string, string];
    props?: [string, string][];
  };
  agenda: { title: string; start: number; events: [number, number, number, string, Tone][] };
  gallery: { title: string; sub: string; items: [string, string, string, Status, string][] };
  wizard: { title: string; steps: string[]; fields: [string, string, string?][] };
  chat: {
    title: string;
    threads: [string, string, string, number][];
    with: [string, string];
    msgs: ['me' | 'them', string, string][];
    side: [string, string][];
  };
  report: { title: string; sub: string; cols: string[]; rows: string[][]; foot: string[] };
}

const BY_LANGUAGE: Record<string, SampleDomain[]> = {
  'pt-BR': ptBR as unknown as SampleDomain[],
};

export function samplesFor(language: string): SampleDomain[] {
  return BY_LANGUAGE[language] ?? BY_LANGUAGE['pt-BR']!;
}

const ICONS: Record<string, LucideIcon> = {
  alert: AlertTriangle,
  bag: ShoppingBag,
  book: BookOpen,
  box: Box,
  calendar: Calendar,
  chart: BarChart3,
  chat: MessageSquare,
  check: Check,
  clock: Clock,
  copy: Copy,
  file: FileText,
  folder: Folder,
  heart: Heart,
  home: Home,
  info: Info,
  okc: CheckCircle2,
  pin: MapPin,
  sliders: SlidersHorizontal,
  target: Target,
  ticket: Ticket,
  truck: Truck,
  user: User,
  users: Users,
  wallet: Wallet,
};

export function sampleIcon(name: string): LucideIcon {
  return ICONS[name] ?? Folder;
}

/** O ícone de um exemplo pelo nome (componente fixo: o React não recria a cada desenho). */
export function SampleIcon({ name, ...props }: { name: string } & LucideProps) {
  return createElement(sampleIcon(name), { 'aria-hidden': true, ...props });
}

/** A etiqueta da base para cada situação do exemplo. */
export const BADGE_TONE: Record<Tone, 'ok' | 'warn' | 'info' | 'off' | 'neutral'> = {
  ok: 'ok',
  warn: 'warn',
  info: 'info',
  bad: 'off',
  neu: 'neutral',
};
